import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Mail, Send, Trash2 } from "lucide-react";
import { avatarForId } from "@/lib/avatars";
import { timeAgo } from "@/lib/lifeos";

export default function ApprovalsPanel({ onClose, googleConnected }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [edited, setEdited] = useState({});

  const load = () => api.getApprovals().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const send = async (q) => {
    setBusy(q.id);
    try { await api.approveSend(q.id, edited[q.id] ?? q.body); toast.success("Reply sent"); setItems((p) => p.filter((x) => x.id !== q.id)); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(null); }
  };
  const dismiss = async (q) => { await api.dismissApproval(q.id); setItems((p) => p.filter((x) => x.id !== q.id)); };

  return (
    <div className="max-w-3xl mx-auto pt-8 px-4 pb-24">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><Mail size={18} /> Approvals · Draft Queue</h2>
        <button data-testid="approvals-close-button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
      </div>
      <p className="text-zinc-500 font-mono text-xs mb-6">Replies your agents pre-drafted on autopilot. Review, edit, then approve to send.</p>

      {!googleConnected && <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4 text-amber-300/90 text-sm font-mono mb-4">Connect Google in App Connections and set an agent's autopilot to "Draft email replies" to fill this queue.</div>}

      {items && items.length === 0 && <div className="text-center text-zinc-600 font-mono text-sm py-16">No drafts waiting. When agents draft replies they'll appear here.</div>}
      {!items && <div className="grid place-items-center py-16 text-cyan-300"><Loader2 className="animate-spin" size={24} /></div>}

      <div className="space-y-3">
        {(items || []).map((q) => (
          <div key={q.id} data-testid="approval-item" className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="rounded-full overflow-hidden shrink-0" style={{ width: 30, height: 30, border: "2px solid #00f0ff", background: "#0b0e14" }}><img src={avatarForId(q.agent_id)} alt="" className="w-full h-full object-cover object-top" /></span>
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 truncate">{q.reply_subject}</div>
                <div className="text-[11px] font-mono text-zinc-500 truncate">to {q.to} · by {q.agent_name} · {timeAgo(q.created_at)} ago</div>
              </div>
            </div>
            <textarea
              data-testid="approval-body"
              defaultValue={q.body}
              onChange={(e) => setEdited((s) => ({ ...s, [q.id]: e.target.value }))}
              rows={6}
              className="w-full bg-[#0b0e14] border border-white/10 rounded-lg p-2 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 thin-scroll"
            />
            <div className="flex gap-2 mt-2">
              <button data-testid="approval-send" onClick={() => send(q)} disabled={busy === q.id} className="flex-1 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center justify-center gap-1">
                {busy === q.id ? <Loader2 size={14} className="animate-spin" /> : <><Send size={13} /> Approve & Send</>}
              </button>
              <button data-testid="approval-dismiss" onClick={() => dismiss(q)} className="w-10 h-9 grid place-items-center rounded-lg bg-white/5 hover:bg-red-500/10 text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
